"use client";

// Dictation seam (plan 027): a tap-to-toggle mic button in the composer’s
// right cluster, driven by a host-supplied `dictation` source. The block
// owns the source SHAPE (`ComposerDictationSource`); the SDK's
// `useMagnusDictation()` returns a structurally matching object, and the
// private layer keeps its own richer voice cluster via C14
// (`renderRightCluster`) — it never passes both.
//
// Visual states port `dictation-button.tsx` from the private layer
// (idle / busy / listening / error) with block-local styles. The private
// layer's level-meter recording pill is NOT ported: the SDK store has no
// audio-level seam, so `listening` is a pulsing mic button (tap to stop).
import { c as _c } from "react/compiler-runtime";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { Loader2Icon, MicIcon, MicOffIcon } from "lucide-react";
import { type RefObject, useEffect, useRef } from "react";
import type { TipTapSkillInputHandle } from "./tiptap-input";

/** Dictation lifecycle, mirroring the SDK's `MagnusDictationState`. */
export type ComposerDictationState = "idle" | "requesting-token" | "listening" | "finalizing" | "error";

/**
 * The composer dictation seam. Absent on `RichComposerProps` → no mic
 * button (the degraded default). Everything is host-supplied so the block
 * stays vendor-agnostic; `useMagnusDictation()` returns a ready-made
 * source.
 *
 * `transcript` carries the latest finalized utterance. Sources clear it
 * on the next `start()`, so each turn is a fresh edge for the composer's
 * insert effect — even when two turns transcribe to the same words.
 */
export interface ComposerDictationSource {
  error?: Error;
  start(): void;
  state: ComposerDictationState;
  stop(): void;
  transcript: string;
}

/**
 * Inserts finalized transcripts into the editor at the cursor. Lives in
 * an effect keyed on the transcript value (not on state transitions) so a
 * transcript that arrives while the editor is remounting still lands.
 */
export function useDictationTranscriptInsert(dictation, editorRef) {
  const $ = _c(4);
  const transcript = dictation?.transcript ?? "";
  const prevTranscriptRef = useRef("");
  let t0;
  let t1;
  if ($[0] !== editorRef || $[1] !== transcript) {
    t0 = () => {
      const prev = prevTranscriptRef.current;
      prevTranscriptRef.current = transcript;
      if (transcript && transcript !== prev) {
        editorRef.current?.insertText(transcript);
      }
    };
    t1 = [transcript, editorRef];
    $[0] = editorRef;
    $[1] = transcript;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}

/** Mic trigger for the composer's right cluster (hidden unless the host
 *  supplies `dictation`). */
export function ComposerDictationButton(t0) {
  const $ = _c(19);
  const {
    dictation,
    disabled
  } = t0;
  const {
    state
  } = dictation;
  const isBusy = state === "requesting-token" || state === "finalizing";
  let t1;
  if ($[0] !== dictation || $[1] !== state) {
    t1 = () => {
      if (state === "idle" || state === "error") {
        dictation.start();
      } else {
        if (state === "listening") {
          dictation.stop();
        }
      }
    };
    $[0] = dictation;
    $[1] = state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onClick = t1;
  let t2;
  if ($[3] !== state) {
    t2 = ariaLabelFor(state);
    $[3] = state;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = state === "listening" && "animate-pulse text-destructive";
  let t4;
  if ($[5] !== t3) {
    t4 = cn("rounded-lg active:scale-[0.96]", t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = disabled || isBusy;
  let t6;
  if ($[7] !== state) {
    t6 = titleFor(state);
    $[7] = state;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isBusy || $[10] !== state) {
    t7 = isBusy ? <Loader2Icon className="size-4 animate-spin" /> : state === "error" ? <MicOffIcon className="size-4" /> : <MicIcon className="size-4" />;
    $[9] = isBusy;
    $[10] = state;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onClick || $[13] !== t2 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <Button aria-label={t2} className={t4} disabled={t5} onClick={onClick} size="icon-sm" title={t6} type="button" variant="ghost">{t7}</Button>;
    $[12] = onClick;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
function ariaLabelFor(state: ComposerDictationState): string {
  switch (state) {
    case "requesting-token":
      return "Starting microphone";
    case "listening":
      return "Stop dictation";
    case "finalizing":
      return "Transcribing dictation";
    case "error":
      return "Retry dictation (microphone error)";
    default:
      return "Start dictation";
  }
}
function titleFor(state: ComposerDictationState): string {
  switch (state) {
    case "requesting-token":
      return "Starting…";
    case "listening":
      return "Tap to transcribe";
    case "finalizing":
      return "Transcribing…";
    case "error":
      return "Microphone unavailable — tap to retry";
    default:
      return "Dictate";
  }
}